/**
 * @fileoverview Settings page.
 * @description The identity/session self-management screen: account summary and the
 * active-sessions panel. GET /identity/me backs the account summary's tenant detail (shown only
 * when present — the real backend doesn't serve it yet). The "Change Password" link opens
 * ChangePasswordModal in place (POST /identity/password/change); the /change-password route is
 * reserved for the forced change after sign-in.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ActiveSessionsPanel, ChangePasswordModal, useCurrentSession } from '@/features/auth'
import LinkButton from '@/shared/components/atoms/LinkButton'
import { useAuth } from '@/shared/lib/auth/useAuth'

/**
 * @component
 */
function SettingsPage() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const currentSessionQuery = useCurrentSession()
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false)

  return (
    <div className="flex max-w-2xl flex-col gap-8">
      <div>
        <h2 className="text-[20px] font-semibold text-ink">{t('settings.title')}</h2>

        <dl className="mt-4 grid max-w-sm grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-[14px]">
          <dt className="text-slate">{t('settings.account.emailLabel')}</dt>
          <dd className="text-ink">{user?.email}</dd>
          <dt className="text-slate">{t('settings.account.roleLabel')}</dt>
          <dd className="text-ink">{user?.activeRoleName}</dd>
          {currentSessionQuery.data?.tenantId && (
            <>
              <dt className="text-slate">{t('settings.account.tenantLabel')}</dt>
              <dd className="text-ink">{currentSessionQuery.data.tenantId}</dd>
            </>
          )}
        </dl>

        <div className="mt-4">
          <LinkButton onClick={() => setIsChangePasswordOpen(true)}>
            {t('settings.changePassword.title')}
          </LinkButton>
        </div>
      </div>

      <ActiveSessionsPanel />

      <ChangePasswordModal
        open={isChangePasswordOpen}
        onClose={() => setIsChangePasswordOpen(false)}
      />
    </div>
  )
}

export default SettingsPage
